import { defineStore } from 'pinia'
import getters from './getters'
import state from './state'
import { markRaw } from 'vue'
import ErrorType from '@/enum/ErrorType'
import type { TToastNotification } from '@/types/state-types/TGlobalState'
import type { TAccountAccessResponse } from '@/types/api-types/account'
import { pluralizeWithoutCount } from '@/utils/helpers'
import { BASE_API_URL } from '@/utils/constants'

const useGlobalStore = defineStore('global', {
  state: () => state,
  getters,
  actions: {
    setAccountData(data: TAccountAccessResponse) {
      this.userId = data.userId
      this.accessToken = data.accessToken
    },
    addError(error: object, errorType: ErrorType) {
      if (!this.errors) this.errors = []
      this.errors.push(markRaw({ error, errorType }))
    },
    clearErrors() {
      this.errors = []
    },
    parseURL(url: any) {
      const urlObj = new URL(BASE_API_URL + url)
      const baseUrl = BASE_API_URL + urlObj.pathname
      const queryParams = new URLSearchParams(urlObj.search)
      const pagingParams = new URLSearchParams()
      const otherParams = new URLSearchParams()

      queryParams.forEach((value, key) => {
        if (key.startsWith('Paging.')) {
          pagingParams.append(key, value)
        } else {
          otherParams.append(key, value)
        }
      })

      return { baseUrl, pagingParams, otherParams }
    },
    getSanitizedKey(controllerKey: any, considerQueryParams: any) {
      const { baseUrl, pagingParams } = this.parseURL(controllerKey)

      if (!considerQueryParams) {
        return baseUrl
      }

      return `${baseUrl}?${pagingParams.toString()}`
    },
    getSanitizedKeyWithOtherFilters(controllerKey: any) {
      const { baseUrl, otherParams } = this.parseURL(controllerKey)
      return `${baseUrl}?${otherParams.toString()}`
    },
    addAbortController(controllerKey: any, considerQueryParams: any) {
      const sanitizedKey = this.getSanitizedKey(controllerKey, considerQueryParams)
      this.removeExistingAbortController(controllerKey, considerQueryParams)

      if (!this.abortControllers) this.abortControllers = new Map()
      this.abortControllers.set(sanitizedKey, new AbortController())

      if (considerQueryParams) {
        const sanitizedKeyWithOtherFilters = this.getSanitizedKeyWithOtherFilters(controllerKey)
        if (!this.abortControllersWithFilters) this.abortControllersWithFilters = new Map()
        this.abortControllersWithFilters.set(sanitizedKeyWithOtherFilters, new AbortController())
      }

      return this.abortControllers.get(sanitizedKey)
    },
    removeExistingAbortController(controllerKey: any, considerQueryParams = true) {
      const sanitizedKey = this.getSanitizedKey(controllerKey, considerQueryParams)

      if (this.abortControllers instanceof Map && this.abortControllers.has(sanitizedKey)) {
        this.abortControllers.get(sanitizedKey)?.abort()
        this.abortControllers.delete(sanitizedKey)
      }

      if (considerQueryParams) {
        const sanitizedKeyWithOtherFilters = this.getSanitizedKeyWithOtherFilters(controllerKey)
        if (
          this.abortControllersWithFilters instanceof Map &&
          this.abortControllersWithFilters.has(sanitizedKeyWithOtherFilters)
        ) {
          this.abortControllersWithFilters.get(sanitizedKeyWithOtherFilters)?.abort()
          this.abortControllersWithFilters.delete(sanitizedKeyWithOtherFilters)
        }
      }
    },
    abortPendingRequests() {
      if (this.abortControllers?.size) {
        this.abortControllers.forEach((controller) => controller?.abort())
        this.abortControllers = new Map()
      }

      if (this.abortControllersWithFilters?.size) {
        this.abortControllersWithFilters.forEach((controller) => controller?.abort())
        this.abortControllersWithFilters = new Map()
      }
    },

    addToastNotification(notification: TToastNotification, shouldStack?: boolean) {
      if (!this.toastNotifications) this.toastNotifications = []
      if (shouldStack) {
        this.toastNotifications.push(notification)
      } else {
        this.toastNotifications = [notification]
      }
    },
    
    removeNotification(targetIndex: number) {
      if (!this.toastNotifications) return
      const timeoutID = setTimeout(
        () => {
          const itemsBeforeTarget = this.toastNotifications?.slice(0, targetIndex)
          const itemsAfterTarget = this.toastNotifications?.slice(targetIndex + 1)
          if (itemsBeforeTarget && itemsAfterTarget)
            this.toastNotifications = [...itemsBeforeTarget, ...itemsAfterTarget]
          clearTimeout(timeoutID)
        },
        1000 * targetIndex + 1
      )
    },

    removeAllNotifications() {
      this.toastNotifications = []
      this.hasNewNotifications = false
    },
    removeRenewRequest() {
      this.renewRequestPromise = null
    },
    setBreadcrumbs(items: { name: string; path: string }[]) {
      this.breadcrumbs = items
    },
    clearBreadcrumbs() {
      this.breadcrumbs = []
    },
    saveOfficerWorker(worker: Worker) {
      this.officerWorker = markRaw(worker)
    },
    saveGeofenceWorker(worker: Worker) {
      this.geofenceWorker = markRaw(worker)
    },
    handleBulkOperationErrorsView(response: any, moduleName: string) {
      this.showBulkOperationErrors = true
      this.bulkOperationErrors = response.data as any
      this.bulkOperationFailedMessage = `${pluralizeWithoutCount(response.data.failed, moduleName)} not archived`
    },
    toggleNotificationsFlyout() {
      this.showNotifications = !this.showNotifications
    }
  }
})
export default useGlobalStore
